import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RightsGuard } from "./components/layout/RightsGuard";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Portfolio from "./pages/Portfolio";
import Funds from "./pages/Funds";
import Settings from "./pages/Settings";
import ImpersonationBanner from "./components/layout/ImpersonationBanner";
import Research from "./pages/MFSchemes";
import Masters from "./pages/Masters";
import BulkClientPrefsUpload from "./pages/BulkClientPrefsUpload";
import FamilyMappingUpload from "./pages/FamilyMappingUpload";
import Cronjobs from "./pages/Cronjobs";
import Mismatch from "./pages/Mismatch";
import ClientWise from "./pages/ClientWise";
import Downloads from "./pages/Downloads";
import AccountCreationMaster from "./pages/AccountCreationMaster";
import Analytics from "./pages/Analytics";
import ResetDB from "./pages/ResetDB";
import CommissionsAdmin from "./pages/Commissions";
import PortfolioStrategy from "./pages/PortfolioStrategy";
import DataFlow from "./pages/DataFlow";
import Leads from "./pages/Leads";
import LeadIntroductionPage from "./pages/LeadIntroductionPage";
import LeadKycInformationPage from "./pages/LeadKycInformationPage";
import LeadAssetAllocationPage from "./pages/LeadAssetAllocationPage";
import LeadAccountOpeningPage from "./pages/LeadAccountOpeningPage";
import LeadMandatePage from "./pages/LeadMandatePage";
import LeadUploadPage from "./pages/LeadUploadPage";
import FirstLoginReset from "./pages/FirstLoginReset";
import ClientDashboard from "./pages/client/ClientDashboard";
import SubBrokerDashboard from "./pages/subbroker/SubBrokerDashboard";
import SubBrokerList from "./pages/subbroker/SubBrokerList";
import SubBrokerForm from "./pages/subbroker/SubBrokerForm";
import AssociateIndex from "./pages/associates/AssociateIndex";
import AssociateWizard from "./pages/associates/AssociateWizard";
import AssociateCreate from "./pages/associates/AssociateCreate";
import EmployeeWizard from "./pages/associates/EmployeeWizard";
import SubBrokerBulkUpload from "./pages/subbroker/SubBrokerBulkUpload";
import SubBrokerUploadHistory from "./pages/subbroker/SubBrokerUploadHistory";
import SubBrokerClientMapping from "./pages/subbroker/SubBrokerClientMapping";

// Public marketing pages
import MarketingHome from "./pages/public/MarketingHome";
import About from "./pages/public/About";
import ForInvestors from "./pages/public/ForInvestors";
import Features from "./pages/public/Features";
import Commissions from "./pages/public/Commissions";
import Pricing from "./pages/public/Pricing";
import ForArnHolders from "./pages/public/ForArnHolders";
import Demo from "./pages/public/Demo";
import ForgotPassword from "./pages/ForgotPassword";
import ForgotLoginId from "./pages/ForgotLoginId";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminLeads from "./pages/admin/AdminLeads";
import PlatformTenants from "./pages/platform/PlatformTenants";
import StartTrial from "./pages/public/StartTrial";

// Layout Components
import Sidebar, { SidebarProvider, useSidebar } from "./components/layout/Sidebar";
import MobileTopBar from "./components/layout/MobileTopBar";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import Header from "./components/layout/Header";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated, api } = useAuth();
  const [secStatus, setSecStatus] = useState(null);
  const [secLoading, setSecLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!isAuthenticated) return;
      setSecLoading(true);
      try {
        const { data } = await api.get("/auth/security-status");
        if (!cancelled) setSecStatus(data);
      } catch {
        if (!cancelled) setSecStatus({ force_password_reset: false, force_pin_reset: false });
      } finally {
        if (!cancelled) setSecLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [isAuthenticated, api]);

  if (loading || (isAuthenticated && secLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember any deep link (e.g. an emailed "verify associate" link) so login lands back on it.
    if (window.location.pathname !== "/") {
      sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search);
    }
    return <Navigate to="/login" replace />;
  }

  // First-login gate: block ALL routes until both password and PIN are reset.
  // BUT: skip this when a platform_owner is impersonating — they aren't the
  // one who should change the tenant owner's password/PIN. The owner sets
  // their own credentials when they click their /setup-account link.
  if (!user?.is_impersonating && secStatus && (secStatus.force_password_reset || secStatus.force_pin_reset)) {
    return <Navigate to="/first-login-reset" replace />;
  }

  // platform_owner (the founder) has access to EVERY route — they're a
  // superset of super_admin. No need to add them to each allowedRoles array.
  if (allowedRoles && user?.role !== "platform_owner" && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/portfolio" replace />;
  }

  return children;
};

// Role-aware dashboard: super-admin sees the big dashboard; sub-broker → Dashboard (scoped);
// client → Portfolio.
const DashboardOrSubBrokerRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/portfolio" replace />;
  // Sub-broker now gets the Dashboard view (backend scopes to mapped clients).
  return <Dashboard />;
};

// Dashboard Layout
const DashboardLayoutContent = () => {
  const { isCollapsed, isMobile } = useSidebar();
  const { user } = useAuth();
  // For clients on mobile, the Portfolio hero acts as the header → no top bar.
  const showMobileTopBar = isMobile && user?.role !== "client";
  return (
    <div className="min-h-screen bg-white dashboard-shell">
      <Sidebar />
      <div
        className={`transition-all duration-300 ${
          isMobile ? "ml-0" : isCollapsed ? "ml-[70px]" : "ml-[260px]"
        }`}
      >
        <ImpersonationBanner />
        {showMobileTopBar && <MobileTopBar />}
        <main
          className={isMobile ? "p-3 sm:p-4" : "p-6"}
          style={isMobile ? { paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" } : undefined}
        >
          <RightsGuard><Outlet /></RightsGuard>
        </main>
      </div>
      <MobileBottomNav />
      <PWAInstallPrompt />
    </div>
  );
};

const DashboardLayout = () => {
  return (
    <SidebarProvider>
      <DashboardLayoutContent />
    </SidebarProvider>
  );
};

// Public Route wrapper — shows the page regardless of auth state. The public
// nav surfaces a "Go to Dashboard" button to logged-in users. Used for the
// /login and /register pages (originally redirected logged-in users to
// /dashboard). For marketing pages we render the page directly without this
// wrapper so the marketing site is always accessible.
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07111F]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#C8A96B] border-t-transparent" />
      </div>
    );
  }
  return children;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <IdleLogout />
      <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<Navigate to="/about" replace />} />
      <Route path="/about" element={<About />} />
      <Route path="/features" element={<Features />} />
      <Route path="/mfd" element={<ForArnHolders />} />
      <Route path="/private-investors" element={<ForInvestors />} />
      <Route path="/commissions-on-mutual-funds" element={<Commissions />} />
      <Route path="/for-investors" element={<Navigate to="/private-investors" replace />} />
      {/* SaaS marketing pages for ARN holders / sub-brokers */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/start-trial" element={<StartTrial />} />
      <Route path="/signup" element={<Navigate to="/start-trial" replace />} />
      <Route path="/for-arn-holders" element={<ForArnHolders />} />
      <Route path="/partners" element={<Navigate to="/for-arn-holders" replace />} />
      <Route path="/demo" element={<Demo />} />
      {/* Legacy redirect */}
      <Route path="/subscribe" element={<Navigate to="/pricing" replace />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* First-login forced reset — outside the dashboard layout, no security-status gate */}
      <Route path="/first-login-reset" element={<FirstLoginReset />} />
      {/* Public associate self-verification (no login) */}
      <Route path="/associate-verify/:token" element={<AssociateWizard selfVerify />} />
      <Route path="/employee-verify/:token" element={<EmployeeWizard selfVerify />} />

      {/* Account recovery */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgot-login-id" element={<ForgotLoginId />} />

      {/* Protected Routes with Dashboard Layout */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={
          <DashboardOrSubBrokerRedirect />
        } />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:clientId" element={<ClientDetail />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/funds" element={<Funds />} />
        <Route
          path="/masters"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker", "sub_broker"]}>
              <Masters />
            </ProtectedRoute>
          }
        />
        <Route
          path="/masters/report-prefs-upload"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <BulkClientPrefsUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/masters/family-mapping-upload"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <FamilyMappingUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mf-schemes"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker", "sub_broker"]}>
              <Research />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cronjobs"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <Cronjobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mismatch"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <Mismatch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-wise"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker", "sub_broker"]}>
              <ClientWise />
            </ProtectedRoute>
          }
        />
        <Route
          path="/downloads"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker", "sub_broker"]}>
              <Downloads />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-master"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <AccountCreationMaster />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reset-db"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <ResetDB />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-flow"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
              <DataFlow />
            </ProtectedRoute>
          }
        />
        <Route
          path="/commissions"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <CommissionsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio-strategy"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <PortfolioStrategy />
            </ProtectedRoute>
          }
        />
        {/* Platform Owner — Tenants Console (multi-tenancy SaaS admin) */}
        <Route
          path="/platform/tenants"
          element={
            <ProtectedRoute allowedRoles={["platform_owner"]}>
              <PlatformTenants />
            </ProtectedRoute>
          }
        />
        {/* Sub Broker Module */}
        <Route path="/sub-brokers/dashboard" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "sub_broker"]}>
            <SubBrokerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/list" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <AssociateIndex />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/legacy-list" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <SubBrokerList />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/create" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <AssociateCreate />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/employee/:id" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <EmployeeWizard />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/associate/:id" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <AssociateWizard />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/edit/:id" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <SubBrokerForm />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/bulk-upload" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <SubBrokerBulkUpload />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/upload-history" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <SubBrokerUploadHistory />
          </ProtectedRoute>
        } />
        <Route path="/sub-brokers/client-mapping" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <SubBrokerClientMapping />
          </ProtectedRoute>
        } />
        <Route path="/client/dashboard" element={
          <ProtectedRoute allowedRoles={["client"]}>
            <ClientDashboard />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={<Settings />} />
        {/* Admin: subscription management */}
        <Route path="/admin/subscriptions" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker"]}>
            <AdminSubscriptions />
          </ProtectedRoute>
        } />
        <Route path="/admin/leads" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <AdminLeads />
          </ProtectedRoute>
        } />
        <Route path="/leads" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <Leads />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/introduction" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadIntroductionPage />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/kyc-information" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadKycInformationPage />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/asset-allocation" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadAssetAllocationPage />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/account-opening" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadAccountOpeningPage />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/mandate" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadMandatePage />
          </ProtectedRoute>
        } />
        <Route path="/leads/:leadId/uploads" element={
          <ProtectedRoute allowedRoles={["super_admin", "broker", "admin", "sub_broker", "employee"]}>
            <LeadUploadPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Legacy / fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

/**
 * ScrollToTop — resets scroll position to (0,0) whenever the route
 * pathname changes, so every page starts at the top instead of
 * inheriting the previous route's scroll offset.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/**
 * IdleLogout — DISABLED per user request (2026-02-09).
 * Previously signed the user out after 5 minutes of inactivity.
 * Sessions now persist until the JWT itself expires (24h) or manual logout.
 */
function IdleLogout() {
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

